import { get, set } from "lodash";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MdOutlineFlaky } from "react-icons/md";
import Form from "shared/components/Form";
import FormField from "shared/components/FormField";
import Input from "shared/components/Input";
import { NAME_SPACES } from "shared/locales/constants";
import { SUBSCRIPTION_FORM_KEYS } from "utils/constants";

const DEFAULT_QUESTIONS = {
  DEFAULT: [
    "project.fields.acceptProductStatuses",
    "project.fields.acceptFees",
    "project.fields.acceptAgreement",
  ],
  TNS: [
    "project.fields.acceptMutualRegulation",
    "project.fields.acceptMembershipRequestCopy",
    "project.fields.acceptWithdrawalTerms",
    "project.fields.confirmSocialProtectionAffiliation",
    "project.fields.confirmDeclaredActivity",
    "project.fields.confirmSocialContributionsUpToDate",
    "project.fields.commitAnnualContributionProof",
    "project.fields.ackDependentsProofObligation",
    "project.fields.confirmInformationAccuracy",
  ],
};

const DataProtection = ({ initialValues, setPercent, bindField }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });
  const ADDITIONAL_INFORMATION = t("SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION", {
    returnObjects: true,
  });
  const MM = ADDITIONAL_INFORMATION.INFORMATION.MUTUAL_MOBILITY;

  const questionLabels = useMemo(
    () => ({
      "project.fields.acceptProductStatuses": MM.STATUSES_AGREEMENT,
      "project.fields.acceptFees": MM.FEES,
      "project.fields.acceptAgreement": MM.AGREEMENT,
      "project.fields.acceptMutualRegulation": MM.MUTUAL_REGULATION_ACK,
      "project.fields.acceptMembershipRequestCopy": MM.MEMBERSHIP_REQUEST_ACK,
      "project.fields.acceptWithdrawalTerms": MM.WITHDRAWAL_TERMS_ACK,
      "project.fields.confirmSocialProtectionAffiliation":
        MM.SOCIAL_PROTECTION_AFFILIATION,
      "project.fields.confirmDeclaredActivity": MM.DECLARED_ACTIVITY_CONFIRM,
      "project.fields.confirmSocialContributionsUpToDate":
        MM.SOCIAL_CONTRIBUTIONS_UPTODATE,
      "project.fields.commitAnnualContributionProof":
        MM.ANNUAL_CONTRIBUTION_PROOF,
      "project.fields.ackDependentsProofObligation": MM.DEPENDENTS_PROOF_ACK,
      "project.fields.confirmInformationAccuracy": MM.INFORMATION_ACCURACY_CERT,
    }),
    [MM],
  );

  const activeQuestions =
    DEFAULT_QUESTIONS[
      get(initialValues, "insurancePlan.fields.tns") ? "TNS" : "DEFAULT"
    ];

  const acceptDigitalPath = "project.contact.acceptDigital";
  const acceptDigitalField = bindField(acceptDigitalPath, {
    label: SUBSCRIPTION.FORM.DIGITAL_OPTION.LABEL,
    onChange: () => setPercent(initialValues),
  });

  return (
    <div
      className="subscription-form--wrapper"
      id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.DATA_PROTECTION}-section`}
    >
      <Form
        type="vertical"
        title={ADDITIONAL_INFORMATION.DATA_PROTECTION.TITLE}
      >
        <div className="grid--1">
          {activeQuestions.map((path) => {
            const field = bindField(path, {
              label: questionLabels[path],
              onChange: () => setPercent(initialValues),
            });
            return (
              <FormField
                key={path}
                path={field.path}
                label={field.label}
                required={field.required}
                disabled={field.disabled}
              >
                {(inputField) => (
                  <Input
                    type="switch"
                    label={field.label}
                    checked={get(initialValues, path) === true}
                    {...inputField}
                    onChange={field.onChange}
                  />
                )}
              </FormField>
            );
          })}
          <FormField
            path={acceptDigitalField.path}
            label={acceptDigitalField.label}
            required={acceptDigitalField.required}
            disabled={acceptDigitalField.disabled}
          >
            {(field) => (
              <Input
                type="radio"
                variant="group"
                icon={<MdOutlineFlaky size={16} />}
                label={acceptDigitalField.label}
                values={[
                  {
                    value: true,
                    label: SUBSCRIPTION.FORM.DIGITAL_OPTION.OPTIONS.YES,
                  },
                  {
                    value: false,
                    label: SUBSCRIPTION.FORM.DIGITAL_OPTION.OPTIONS.NO,
                  },
                ]}
                value={get(initialValues, acceptDigitalPath)}
                {...field}
                onChange={acceptDigitalField.onChange}
              />
            )}
          </FormField>
        </div>
      </Form>
    </div>
  );
};

export default DataProtection;
